import { SubscriptionTypes } from 'appConstants';
import { getPackageIDList } from 'helpers/packageIDsList';

export type PackagesList = PackageGroup[];

export type PackageGroup = {
  name: string;
  description: string;
  subjects: Subjects[];
  classList?: any;
  selected?: string;
  subject?: string;
};

export type Subjects = {
  name: string;
  subscriptions: Package[];
};

export type Package = {
  packageID: number;
  packageIDs: number[];
  type: SubscriptionTypes.INVESTEMENT | SubscriptionTypes.STANDARD;
  price: number;
  multiPurchaseDiscount: number;
  promoCodeDiscount: number;
  subscribed: boolean;
  isAvailableForSchool: boolean;
  isFreeTrial: boolean;
  trialRemainingDays: number | null;
  ageGroup?: string;
  subject?: string;
};

export type PackagePricesList = PackagePrices[];

export type PackagePrices = {
  packagesID: number;
  discountedPrice: number;
  discountType: number;
  percentageOff: number;
  amountOff: number;
};

export interface IPromoCode {
  value: string;
  error: string;
}

export interface IPaymentTypes {
  poNumber: string;
  byCard: boolean;
  byInvoice: boolean;
  FinanceEmail:  string;
}

export interface IPriceInfo {
  price: any;
  vat: any;
  total: any;
  discount: any;
  appliedDiscountType: any;
}

export interface ICardInfo {
  cardholderName: string;
  card: any;
}

export interface IUpdateAfter3DSecure {
  tid: number;
  stripePaymentIntentID: string;
  stripePaymentIntentID1: string;
  stripeChargeResponse: string;
  stripeChargeStatus: string;
  stripePaymentIntentStatus: string;
  stripeReceiptUrl: string;
  bPaid: true;
  purchaseHistoryStatusType: string;
}

export enum NewSubscriptionStep {
  CHOOSE_PACKAGE,
  PAYMENT_TYPE,
  PAYMENT_DETAILS,
  CONFIRMATION,
}

export class NewSubscriptionParser {
  public static getPageHeaderInfo = (step: number) => {
    switch (step) {
      case NewSubscriptionStep.CHOOSE_PACKAGE:
        return { title: 'Choose your package', nextStep: 'Payment type' };

      case NewSubscriptionStep.PAYMENT_TYPE:
        return {
          title: 'PaymentType',
          nextStep: 'Payment details / Confirmation',
        };

      case NewSubscriptionStep.PAYMENT_DETAILS:
        return { title: 'Payment details', nextStep: '' };

      case NewSubscriptionStep.CONFIRMATION:
        return { title: 'Confirmation', nextStep: '' };

      default:
        return { title: '', nextStep: '' };
    }
  };

  public static createEmptyPaymentType = (financeEmail: string | null) => ({
    poNumber: '',
    byCard: false,
    byInvoice: false,
    FinanceEmail: financeEmail ? financeEmail : '',
  });

  public static buildArchiveNowPayload = (
    promoCode: string,
    packages: Package[],
    expectedGrossAmount: number,
    poNumber: string,
    FinanceEmail: string
  ) => ({
    promoCode,
    expectedGrossAmount,
    packageIDs: getPackageIDList(packages),
    poNumber,
    FinanceEmail,
  });

  public static buildUpgradeByCardPayload = (
    promoCode: string,
    packages: Package[],
    total: number,
    token: any,
    poNumber: string,
    FinanceEmail: string,
  ) => ({
    promoCode,
    packageIDs: getPackageIDList(packages),
    expectedGrossAmount: total,
    nameOnCard: token.card.name,
    cardEndDigits: token.card.last4,
    expiryDate: `${
      token.card.exp_month.toString().length === 1 && '0'
    }${token.card.exp_month.toString()}/${token.card.exp_year.toString()[2]}${
      token.card.exp_year.toString()[3]
    }`,
    stripeToken: token.id,
    poNumber,
    FinanceEmail,
  });

  public static buildUpdateAfter3DSecurePayload = (response: any) => {
    return {
      tid: response.tid,
      stripePaymentIntentID: response.error
        ? response.error.payment_intent.id
        : response.paymentIntent.id,
    };
  };

  public static parseError = (errors: any) => {
    let error: string;
    switch (errors[0].messageCode) {
      case 'Missing_PromoCode':
        error = 'Promo code is missing';
        break;
      case 'Invalid_PromoCode':
        error = 'Invalid promo code';
        break;
      case 'No_valid_package_selected':
        error = 'The selected packages are invalid';
        break;
      case 'payment_amount_mismatch':
        error = 'Sorry something went wrong. Please try again.';
        break;
      case 'pkey_general_invalid_payment_token':
        error = 'Sorry something went wrong. Please try again.';
        break;
      case 'Invalid_PromoCode_NotStarted':
        error = 'Invalid promocode ';
        break;
      case 'Invalid_PromoCode_Expired':
        error = 'Promo code is expired ';
        break;
      case 'Invalid_PromoCode_UsedAlready':
        error = 'Invalid promocode ';
        break;
      case 'Invalid_School_Account':
        error = 'Invalid school account';
        break;
      case 'Invalid_PackageID':
        error = 'Invalid packages';
        break;
      case 'Invalid_SubscriptionHistory':
        error = 'Invalid subscription history';
        break;
      case 'pkey_general_payment_card_error':
        error = 'Payment failed. Please try again.';
        break;
      case 'pkey_general_payment_error':
        error = 'Payment failed. Please try again.';
        break;
      case 'pkey_general_payment_subscription_failed':
        error = 'Payment failed. Please try again.';
        break;
      case 'pkey_general_payment_failed':
        error = 'Payment failed. Please try again.';
        break;
      case 'Required':
        error = 'Sorry something went wrong. Please try again.';
        break;
      default:
        error = 'Sorry something went wrong. Please try again.';
    }
    return error;
  };
}
